import React from 'react';

import { Shimmer } from './phConfigSkeletons';

/**
 * One skeleton per season card on the Seasons list — mirrors the real card in
 * productionHeadSeason.tsx class for class (padding, header, 5-cell grid, footer), with each text
 * line wrapped in a box of its real line height (text-xs 16px / text-sm 20px, .h6 22.4px → 28px).
 */
export default function SeasonCardSkeleton({ count = 1 }: { count?: number }) {
  return (
    <div
      className="space-y-4 md:space-y-6"
      aria-hidden
    >
      {Array.from({ length: count }).map((_, idx) => (
        <div
          key={idx}
          className="season-card rounded-lg bg-white p-4 md:p-6"
        >
          {/* Header */}
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between md:mb-6">
            <div className="flex items-start space-x-3 sm:space-x-4">
              <Shimmer className="h-10 w-10 shrink-0 sm:h-12 sm:w-12" />
              <div className="min-w-0 flex-1">
                {/* h2.h6 — 16px/1.4 on mobile, 20px/1.4 from md */}
                <div className="flex h-[22.4px] items-center md:h-7">
                  <Shimmer className="h-4 w-56 md:h-5" />
                </div>
                {/* crop · start · end row (text-xs 16px / sm:text-sm 20px) */}
                <div className="mt-1 flex flex-col gap-1 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
                  <div className="flex h-4 items-center sm:h-5">
                    <Shimmer className="h-3 w-28" />
                  </div>
                  <span
                    className="mx-2 hidden h-4 border-l border-gray-300 sm:inline-block"
                    aria-hidden="true"
                  />
                  <div className="flex h-4 items-center gap-3 sm:h-5">
                    <Shimmer className="h-3 w-20" />
                    <Shimmer className="h-3 w-24" />
                  </div>
                </div>
              </div>
            </div>
            {/* Add-agronomist button (!h-9) + three-dot menu */}
            <div className="flex shrink-0 items-center gap-2 self-end sm:self-auto">
              <Shimmer className="h-9 w-40 rounded-lg" />
              <Shimmer className="h-9 w-9 rounded-lg" />
            </div>
          </div>

          <div className="border-b" />

          {/* Season grid — 5 cells in grid-cols-2 / sm:3 / lg:6 */}
          <div className="grid grid-cols-2 gap-4 rounded-lg py-3 sm:grid-cols-3 lg:grid-cols-6">
            {[
              { label: 'w-24', lines: ['w-32'] },
              { label: 'w-28', lines: ['w-28', 'w-24'] },
              { label: 'w-20', lines: ['w-24'] },
              { label: 'w-28', lines: ['w-8'] },
              { label: 'w-24', lines: ['w-8'] },
            ].map((cell, cellIdx) => (
              <div key={cellIdx}>
                <div className="mb-1.5 flex h-4 items-center sm:h-5">
                  <Shimmer className={`h-3 ${cell.label}`} />
                </div>
                <div className="pt-1">
                  {cell.lines.map((width, lineIdx) => (
                    <div
                      key={lineIdx}
                      className="flex h-4 items-center sm:h-5"
                    >
                      <Shimmer className={`h-3 ${width}`} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Footer — "Created on …" */}
          <div className="flex h-4 items-center sm:h-5">
            <Shimmer className="h-3 w-40" />
          </div>
        </div>
      ))}
    </div>
  );
}
